/**
 * @file AdminSummaryChips.tsx
 * @description Cross-customer chips at the top of the admin landing
 *   page: active customers · projects awaiting admin · files in review
 *   · actionable quotes. Sibling to dashboard/SummaryChips (customer
 *   side). Backed by GET /admin/dashboard which runs the four count
 *   queries server-side in parallel.
 * @module Components
 * @security Mounted only inside CustomersList which itself renders only
 *   for admin/staff (dashboard role split). Server enforces admin
 *   role on /admin/* regardless.
 */

import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { getAdminDashboard, type AdminDashboardSummary } from '../../lib/admin-api';

export function AdminSummaryChips(): JSX.Element | null {
  const { t } = useTranslation();
  const [summary, setSummary] = useState<AdminDashboardSummary | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getAdminDashboard()
      .then((s) => {
        if (!cancelled) setSummary(s);
      })
      .catch(() => {
        if (!cancelled) setSummary(null);
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!loaded || summary === null) return null;
  return (
    <div className="flex flex-wrap gap-3" role="status" aria-label={t('admin_summary.aria')}>
      <Chip
        tone="slate"
        hero
        value={summary.active_customers}
        label={t('admin_summary.active_customers')}
      />
      {summary.projects_awaiting_admin > 0 ? (
        <Chip
          tone="amber"
          value={summary.projects_awaiting_admin}
          label={t('admin_summary.projects_awaiting_admin')}
          title={t('admin_summary.projects_awaiting_admin_tooltip')}
        />
      ) : null}
      {summary.files_in_review > 0 ? (
        <Link to="/admin/files-review" className="contents">
          <Chip
            tone="amber"
            value={summary.files_in_review}
            label={t('admin_summary.files_in_review')}
            title={t('admin_summary.files_in_review_tooltip')}
          />
        </Link>
      ) : null}
      {summary.actionable_quotes > 0 ? (
        <Chip
          tone="blue"
          value={summary.actionable_quotes}
          label={t('admin_summary.actionable_quotes')}
          title={t('admin_summary.actionable_quotes_tooltip')}
        />
      ) : null}
      {summary.overdue_milestones > 0 ? (
        <Chip
          tone="red"
          value={summary.overdue_milestones}
          label={t('admin_summary.overdue_milestones')}
          title={t('admin_summary.overdue_milestones_tooltip')}
        />
      ) : null}
      {summary.overdue_projects > 0 ? (
        <Link to="/projects" className="contents">
          <Chip
            tone="red"
            value={summary.overdue_projects}
            label={t('admin_summary.overdue_projects')}
            title={t('admin_summary.overdue_projects_tooltip')}
          />
        </Link>
      ) : null}
    </div>
  );
}

const TONE: Record<'slate' | 'blue' | 'amber' | 'red', string> = {
  slate: 'text-slate-700',
  blue: 'text-blue-700',
  amber: 'text-amber-600',
  red: 'text-red-600',
};

interface ChipProps {
  tone: keyof typeof TONE;
  value: number;
  label: string;
  title?: string;
  /** Marks the page's single hero metric (brand-gradient number). */
  hero?: boolean;
}

function Chip({ tone, value, label, title, hero }: ChipProps): JSX.Element {
  return (
    <span className="card px-4 py-3 min-w-[8.5rem] inline-flex flex-col gap-0.5" title={title}>
      <span
        className={`text-2xl font-semibold ${hero === true ? 'text-brand-gradient' : TONE[tone]}`}
      >
        {value}
      </span>
      <span className="text-xs text-slate-500">{label}</span>
    </span>
  );
}
